import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React, { FC, ReactNode } from 'react'

import { Box } from '../box'
import type { TableCellAlign } from '../table-header-cell'

export type { TableCellAlign }

export type TableCellProps = {
    /** Content alignment — numbers/times right, codes/text left, icons centered. */
    align?: TableCellAlign
    /** Highlight (zebra/striped) cell — fills with `surface/highlight`. */
    background?: boolean
    /** Hover state — brightens the resting background. */
    isHover?: boolean
    className?: string
    children?: ReactNode
    testID?: string
}

const tableCell = tva({
    base: 'h-[50px] justify-center',
    variants: {
        // Alignment + Figma padding: right hugs the right edge (pl-2 pr-1), left
        // the left edge (pl-1 pr-2), centre is symmetric (px-1).
        align: {
            left: 'items-start gap-2.5 pl-1 pr-2',
            center: 'items-center gap-2 px-1',
            right: 'items-end gap-2.5 pl-2 pr-1',
        },
        background: { true: '', false: '' },
        isHover: { true: '', false: '' },
    },
    compoundVariants: [
        // Zebra fill (brighter on hover); a plain cell darkens toward the canvas on hover.
        { background: true, isHover: false, class: 'bg-surface-highlight' },
        { background: true, isHover: true, class: 'bg-surface-highlight-hover' },
        { background: false, isHover: true, class: 'bg-[#1b1b1e]' },
    ],
})

/**
 * Table body cell (Figma node 7714:127168 — `position` × `background`). A
 * 50px-tall, vertically-centred container that aligns its content to the column
 * edge and optionally paints a `surface/highlight` zebra background (with a
 * brighter hover). Hover on a plain cell darkens toward the canvas. Holds a
 * `FlightTableCellContent` (or any node). (AOS-629)
 */
export const TableCell: FC<TableCellProps> = ({
    align = 'center',
    background = false,
    isHover = false,
    className,
    children,
    testID,
}) => (
    <Box className={tableCell({ align, background, isHover, class: className })} testID={testID}>
        {children}
    </Box>
)

TableCell.displayName = 'TableCell'
